import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios'; // Make sure to install axios via npm/yarn
import API_BASE_URL from '../../api/config';

const backgroundImageUrl = ''; // Add background if needed

const ArrowRightIcon = () => (
    <svg className="arrow-icon" viewBox="0 0 24 24" fill="currentColor">
        <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z"/>
    </svg>
);

const ExploreEthiccraft = () => {
    const [slidesData, setSlidesData] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isIntersecting, setIsIntersecting] = useState(false);
    const componentRef = useRef(null);
    const sliderRef = useRef(null);
    
    // Refs for touch handling
    const touchStartX = useRef(0);
    const touchEndX = useRef(0);

    // Fetch slides from API
    useEffect(() => {
        const fetchSlides = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/explore/read_isShown`);
                const arr = Array.isArray(res.data) ? res.data : (res.data.data || []);
                setSlidesData(arr);
            } catch (error) {
                console.error('Error fetching slide data:', error);
            }
        };
        fetchSlides();
    }, []);

    // Observer for lazy loading animation
    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsIntersecting(true);
                    observer.unobserve(entry.target);
                }
            },
            { threshold: 0.1 }
        );

        if (componentRef.current) {
            observer.observe(componentRef.current);
        }

        return () => {
            if (componentRef.current) {
                // eslint-disable-next-line react-hooks/exhaustive-deps
                observer.unobserve(componentRef.current);
            }
        };
    }, []);

    // Autoplay
    useEffect(() => {
        if (!isIntersecting || slidesData.length === 0) return;

        const interval = setInterval(() => {
            setCurrentIndex((prevIndex) => (prevIndex + 1) % slidesData.length);
        }, 5000);

        return () => clearInterval(interval);
    }, [isIntersecting, slidesData]);

    const goToSlide = (index) => {
        setCurrentIndex(index);
    };

    const nextSlide = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % slidesData.length);
    };

    const prevSlide = () => {
        setCurrentIndex((prevIndex) => (prevIndex - 1 + slidesData.length) % slidesData.length);
    };

    useEffect(() => {
        if (sliderRef.current && slidesData.length > 0) {
            const slideWidth = sliderRef.current.querySelector('.slide-card')?.offsetWidth || 0;
            const gap = 20;
            sliderRef.current.style.transform = `translateX(calc(-${currentIndex * (slideWidth + gap)}px + 30% - ${slideWidth / 2}px))`;
        }
    }, [currentIndex, slidesData]);

    // --- Touch Handling Logic ---
    const handleTouchStart = (e) => {
        touchStartX.current = e.targetTouches[0].clientX;
    };
    
    const handleTouchMove = (e) => {
        touchEndX.current = e.targetTouches[0].clientX;
    };
    
    const handleTouchEnd = () => {
        // Minimum swipe distance
        const swipeThreshold = 50; 
        
        if (touchStartX.current - touchEndX.current > swipeThreshold) {
            // Swiped left
            nextSlide();
        } else if (touchStartX.current - touchEndX.current < -swipeThreshold) {
            // Swiped right
            prevSlide();
        }
    };
    // --- End of Touch Handling Logic ---

    return (
        <section
            ref={componentRef}
            className={`travel-showcase-container ${isIntersecting ? 'animate-in' : ''}`}
            style={{ backgroundImage: `url(${backgroundImageUrl})` }}
        >
            <div className="content-overlay">
                <div className="text-content">
                    <h1 className="main-destination-title">
                        <span>Explore</span> Ethiccraft
                    </h1>
                    <p className="destination-description">
                        Be part of our growing community through Ethiccraft Chapters — student-led groups across colleges that bring our vision to life. Each chapter is a hub of creativity, culture, and collaboration, hosting events, workshops, and experiences that reflect the spirit of Ethiccraft. Together, we explore, express, and elevate local talent while staying connected to a larger mission.
                        <br />
                    </p>
                    <a href='/Chapters' className="explore-link">
                        <button className="explore-button">
                            Explore <ArrowRightIcon />
                        </button>
                    </a>
                </div>

                <div 
                    className="image-slider-wrapper"
                    // Add touch event listeners to the slider wrapper
                    onTouchStart={handleTouchStart}
                    onTouchMove={handleTouchMove}
                    onTouchEnd={handleTouchEnd}
                >
                    <div className="slider-track-container">
                        <div className="slider-track" ref={sliderRef}>
                            {slidesData.map((slide, index) => (
                                <div
                                    key={slide.id}
                                    className={`slide-card ${index === currentIndex ? 'active' : ''} ${Math.abs(index - currentIndex) === 1 ? 'adjacent' : ''} ${index < currentIndex ? 'prev-slide-style' : ''} ${index > currentIndex ? 'next-slide-style' : ''}`}
                                    onClick={() => goToSlide(index)}
                                >
                                    <img src={`https://ethiccraft.org/${slide.image}`} alt={slide.title} className="slider-image" />

                                    <div className="slide-info">
                                        <h3 className="slide-title">{slide.title}</h3>
                                        <p className="slide-country">{slide.venue}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    <button onClick={prevSlide} className="slider-nav prev_btn">‹</button>
                    <button onClick={nextSlide} className="slider-nav next_btn">›</button>

                    <div className="slider-dots">
                        {slidesData.map((_, index) => (
                            <button
                                key={index}
                                className={`dot ${index === currentIndex ? 'active' : ''}`}
                                onClick={() => goToSlide(index)}
                                aria-label={`Go to slide ${index + 1}`}
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ExploreEthiccraft;